import { useContext, useState } from 'react';
import {
  AddressElement,
  AddressElementProps,
  useElements,
} from '@stripe/react-stripe-js';
import styled from 'styled-components/macro';
import { ModalButton } from '../../../../SubComponents/Buttons';
import { Label, Text } from '../../../../SubComponents/Text';
import { graphqlSdk } from '../../../../Utils/graphql-client';
import useBookingTracking from '../../../../Hooks/useBookingTracking';
import useTypeSafeLocalize from '../../../../Hooks/use-type-safe-localize';
import {
  ErrorLocalization,
  LocalizationCategory,
  ProductLocalization,
  isErrorLocalization,
} from '../../../../localizations/types';
import AdditionalCustomerDataForm from './additional-customer-data-form';
import { bookingState } from '../../client-state/booking-state-context';
import { paymentReducerActionTypes } from '../../../../redusers/payment-reducer';
import { customerReducerActionTypes } from '../../../../redusers/customer-reducer';
import useBookingMinimalShop from '../../server-data/use-booking-minimal-shop';

const StyledAddressElement = styled(AddressElement)`
  margin-bottom: 30px;
`;

const ConfirmationForm = () => {
  const { state, dispatch } = useContext(bookingState);
  const [showFiscalCodeField, setShowFiscalCodeField] = useState(false);
  const [addressFormComplete, setAddressFormComplete] = useState(false);
  const localize = useTypeSafeLocalize();
  const [loading, setLoading] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const [pinError, setPinError] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const track = useBookingTracking();
  const { data: minimalShop } = useBookingMinimalShop();
  const elements = useElements();

  const isShopCountryItaly = minimalShop?.country === 'Italy';

  const handleZeroBalance = async () => {
    if (!state.booking.id || !state.booking.customerId) return;

    setLoading(true);
    setError(null);
    try {
      if (isShopCountryItaly) {
        if (!elements) {
          // Stripe.js has not yet loaded.
          // Make sure to disable form submission until Stripe.js has loaded.
          return;
        }

        // Check https://github.com/stripe/react-stripe-js/issues/414#issuecomment-1615202528
        // for why elements.submit() is called
        const { error: validationError } = await elements.submit();
        // The Stripe Payment Element automatically displays the error
        if (validationError) return setLoading(false);
        const addressElement = elements.getElement('address');

        if (!addressElement) {
          throw new Error('Address element not found');
        }

        const { value } = await addressElement.getValue();

        await graphqlSdk.booking_updateAndValidateCustomer({
          input: {
            firstname: value.firstName,
            lastname: value.lastName,
            email: state.customer.customer.email,
            phone: state.customer.customer.phone,
            personalIdentificationNumber: state.customer.customer.pin,
            address: {
              streetAddress: `${value.address.line1 || ''} ${
                value.address.line2 || ''
              }`.trim(),
              zip: value.address.postal_code,
              city: value.address.city,
              country: value.address.country,
            },
          },
          id: state.booking.customerId,
        });
      }

      await graphqlSdk.booking_confirmBooking({
        bookingId: state.booking.id,
        subToNewsletter: state.terms.newsletterSubscribed,
      });

      track({ type: 'TRANSACTION_COMPLETE', product: state.booking });
      dispatch({ type: paymentReducerActionTypes.UPDATE_IS_PAID, payload: true });
      setConfirmed(true);
      setLoading(false);
    } catch (error: any) {
      setLoading(false);
      if (/^invalid-condition-/.test(error?.response?.errors?.[0]?.extensions?.code)) {
        setError(ErrorLocalization.PersonalDetailsInvalid);
        if (
          error?.response?.errors?.[0]?.extensions?.code === 'invalid-condition-pinValid'
        ) {
          setPinError(true);
        }
      } else {
        setError('BookingNotCreated');
      }
    }
  };

  const addressElementProps: AddressElementProps = {
    options: {
      mode: 'billing',
      fields: {
        phone: 'never',
      },
      display: {
        name: 'split',
      },
      defaultValues: {
        firstName: state.customer.customer.firstname,
        lastName: state.customer.customer.lastname,
        address: {
          country:
            state.customer.customer?.address?.country ?? minimalShop?.country ?? '',
        },
      },
    },
    onChange: (event) => {
      if (event.value.address.country === 'IT') {
        setShowFiscalCodeField(true);
      } else if (event.value.address.country !== 'IT') {
        setShowFiscalCodeField(false);

        // Reset pin when hiding the field
        if (state.customer.customer.pin) {
          dispatch({
            type: customerReducerActionTypes.UPDATE_CUSTOMER,
            payload: { ...state.customer.customer, pin: '' },
          });
        }
      }

      if (event.complete) {
        setAddressFormComplete(true);
      } else if (!event.complete) {
        setAddressFormComplete(false);
      }
    },
  };

  return (
    <>
      <Text>
        {localize(LocalizationCategory.product, ProductLocalization.neutralBalance)}
      </Text>
      {isShopCountryItaly ? <StyledAddressElement {...addressElementProps} /> : null}

      {isShopCountryItaly && showFiscalCodeField ? (
        <AdditionalCustomerDataForm pinError={pinError} setPinError={setPinError} />
      ) : null}

      {error && (
        <Label error mb="3" mt="3">
          {localize(
            LocalizationCategory.error,
            isErrorLocalization(error) ? error : ErrorLocalization.UnknownError
          )}
        </Label>
      )}

      <ModalButton
        id="confirmBookingButton"
        isLoading={loading}
        onClick={handleZeroBalance}
        disabled={confirmed || (isShopCountryItaly && !addressFormComplete)}
      >
        {localize(LocalizationCategory.product, ProductLocalization.confirmBooking)}
      </ModalButton>
    </>
  );
};

export default ConfirmationForm;
